/**
 * Blur gating, the dirty-lens popup, and the switches that decide whether they run.
 *
 * Wired into the video capture modules, and off unless the client's config turns
 * it on -- see setBlurFromConfig, which each of them calls with the config the
 * same way the flash is switched on from config["torch"]. Two keys, two levels:
 *
 *   "blurred": true               gating AND the popup, as described below
 *   "blurredNotification": true   the popup only -- frames are analysed until
 *                                 the popup has been shown, so its last-5-frames
 *                                 window can be judged, but nothing is dropped
 *                                 and the dial is never held grey
 *
 * Gating:
 *
 *   on    in the video modules, every frame that is going to be uploaded is
 *         analysed first -- guidance frames and interim frames alike: a frame
 *         judged blurry is NOT uploaded, and while a region's frames are blurry
 *         its dial segment is held grey instead of going blue/green. That hold
 *         is one-way: once a segment has been drawn blue or green it is never
 *         taken back. StencilVideo checks nothing per frame; it judges only the
 *         frames it keeps -- the stencil shots at the two moments a stencil
 *         changes colour, and its interim frames.
 *   off   nothing here runs and uploads are not gated -- the app behaves exactly
 *         as it did before this feature existed.
 *
 * The torch is a separate, independent switch: it reads brightness, this reads
 * focus, and both are served by one cached analysis of the frame, so running
 * them together costs no more than running either alone.
 *
 * Blurry frames are skipped and the next frame is tried -- a region can always
 * complete once the user holds steady, so there is no way to get stuck.
 *
 * Everything is recorded per region so the on-screen panel can show what was
 * judged and why. Never throws: a failed analysis is reported as "unknown" and
 * does not block an upload.
 */

import { analyzeImageQuality, boxesFromDetections } from "./imageQuality";

export const MODES = { TORCH: "torch", BLUR: "blur", NORMAL: "normal" };

// The order setMode validates against, and the order cycleMode/stepMode follow.
export const MODE_ORDER = [MODES.TORCH, MODES.BLUR, MODES.NORMAL];

// Blur verdicts come from analyzeImageQuality's own flags, both votes of the 4x4
// grid: `blurry` (at least 14 of 16 cells) gates the upload and the dial, and
// `popupBlurry` (at least 8 of 16) is what the dirty-lens window counts.
// Keeping the decision there means the blur_score / tenengrad / cells columns
// always explain it.
const FRAME_LOG_SIZE = 8; // recent frames kept for the panel

// "Is the lens actually dirty?" check, separate from the per-frame upload gate
// above. Individual blurry frames are normal -- the user moves, the camera hunts
// for focus -- and skipping them is enough. What this catches is the case a skip
// cannot fix: a smeared or fogged lens, where frame after frame comes back blurry
// and the inspection can never complete.
//
// The same shape as the torch's switch-on vote: a rolling window of the last
// POPUP_WINDOW_SIZE analysed frames, and the popup is raised only when ALL of
// them came back blurry. One sharp frame in the window is enough to say the lens
// is fine and stay out of the user's way. The window rolls for the whole capture,
// not just its opening seconds.
//
// Raised at most once per inspection: once shown, no further frames are counted
// and dismissing it does not re-arm it. resetBlur is the only thing that does,
// for a new inspection.
const POPUP_WINDOW_SIZE = 5;

// Off until a client's config asks for it. NORMAL rather than TORCH because the
// flash is no longer one of this switch's positions -- torchController owns that
// independently -- so the only question left here is gating on or off.
let mode = MODES.NORMAL;
// The dirty-lens popup's own switch. On whenever gating is, and on by itself for
// a client that only asked for the notification -- see setBlurFromConfig.
let notify = false;

let frames = []; // recent verdicts, newest last
let events = []; // mode switches and blocked uploads
let regionStats = {}; // region -> { seen, blurry, sharp }
let lastVerdict = null; // verdict for the most recent analysed frame
// Regions whose dial segment has already been drawn blue or green. Blur can hold
// a grey segment grey, but it can never take a colour back once the user has seen
// it, so these are exempt from masking for the rest of the inspection.
let revealed = {};
let blockedUploads = 0;
let analysing = false; // guards against overlapping analyses
let sampling = false; // same, for sampleFrameForBlurWindow

// Window state for the dirty-lens check above.
let popupFrames = []; // popupBlurry verdicts of the last POPUP_WINDOW_SIZE frames, newest last
let popupDone = false; // the popup has been raised; nothing more is counted
let lastWindow = null; // the window that raised the popup, kept for the popup/panel
let alertRaised = false; // set when the window raised the popup, until acknowledged

const hhmmss = () => new Date().toTimeString().slice(0, 8);

const pushEvent = (msg) => {
  events.push(`${hhmmss()} ${msg}`);
  if (events.length > 20) events.shift();
};

export const getMode = () => mode;
export const isTorchMode = () => mode === MODES.TORCH;
export const isBlurMode = () => mode === MODES.BLUR;
export const isNormalMode = () => mode === MODES.NORMAL;

/** Switch modes. Anything unrecognised falls back to torch. Returns the mode. */
export const setMode = (next) => {
  const wanted = MODE_ORDER.includes(next) ? next : MODES.TORCH;
  if (wanted === mode) return mode;
  const from = mode;
  mode = wanted;
  // A verdict from the previous mode must not gate the first frames of this one.
  lastVerdict = null;
  frames = [];
  pushEvent(`MODE ${from.toUpperCase()} -> ${mode.toUpperCase()}`);
  return mode;
};

/**
 * Turn blur gating on or off for this client, from config["blurred"].
 *
 * The flag has to be present AND true. Absent, or present and false, leaves
 * gating off, and everything else in here is then a no-op -- a client without
 * the flag pays nothing beyond a returned null.
 */
export const setBlurEnabled = (enabled) => {
  notify = !!enabled;
  return setMode(enabled ? MODES.BLUR : MODES.NORMAL);
};

/**
 * Switch blur on to the level the client's config asks for.
 *
 *   "blurred": true               gating and the popup
 *   "blurredNotification": true   the popup only
 *
 * Each key has to be present AND true. With neither, everything here stays off.
 */
export const setBlurFromConfig = (config) => {
  const flag = (key) =>
    !!config && Object.keys(config).includes(key) && !!config[key];
  const gate = flag("blurred");
  setBlurEnabled(gate);
  notify = gate || flag("blurredNotification");
  return mode;
};

/**
 * Does anything here still need frames analysed? Gating needs every frame for the
 * whole inspection; the popup only until it has been raised, so a popup-only
 * client stops paying for the analysis once the popup has been shown.
 */
const isBlurActive = () => mode === MODES.BLUR || (notify && !popupDone);

/** Next mode in MODE_ORDER, wrapping. */
export const cycleMode = () =>
  setMode(MODE_ORDER[(MODE_ORDER.indexOf(mode) + 1) % MODE_ORDER.length]);

/**
 * Step one position through the swipe order. dir > 0 moves right, dir < 0 left.
 * Clamped rather than wrapped, so a drag cannot skip past the end and reappear at
 * the other side.
 */
export const stepMode = (dir) => {
  const i = MODE_ORDER.indexOf(mode);
  const next = Math.min(MODE_ORDER.length - 1, Math.max(0, i + (dir > 0 ? 1 : -1)));
  return setMode(MODE_ORDER[next]);
};

/** Clear everything for a new inspection. Leaves the chosen mode alone. */
export const resetBlur = () => {
  frames = [];
  events = [];
  regionStats = {};
  lastVerdict = null;
  revealed = {};
  blockedUploads = 0;
  analysing = false;
  sampling = false;
  popupFrames = [];
  popupDone = false;
  lastWindow = null;
  alertRaised = false;
};

const statsFor = (region) => {
  const key = region || "unknown";
  if (!regionStats[key]) regionStats[key] = { seen: 0, blurry: 0, sharp: 0 };
  return regionStats[key];
};

/** Fold one analysed frame into the window, dropping the oldest past five. */
const noteWindowFrame = (blurred) => {
  if (popupDone) return;
  popupFrames.push(!!blurred);
  if (popupFrames.length > POPUP_WINDOW_SIZE) popupFrames.shift();
};

/**
 * Analyse one frame and record the verdict against its region.
 *
 * Runs on every frame while blur is on -- gating or popup-only, since the popup's
 * window is judged on these same verdicts. Not throttled like the torch check,
 * because the answer gates whether THIS frame may be uploaded, so a stale verdict
 * would gate the wrong frame. Overlapping calls are skipped rather than queued.
 *
 * Resolves { blurred, unknown, blurScore, tenengradScore }. `unknown` means the
 * analysis could not run, and the caller should let the frame through.
 */
export const checkFrameForBlur = async (
  base64,
  detections,
  videoW,
  videoH,
  region
) => {
  if (!isBlurActive()) return null;
  if (!base64 || !videoW || !videoH) return null;
  if (analysing) return lastVerdict; // reuse the newest verdict rather than queue
  analysing = true;

  try {
    // Same boxes as the torch check, so both read one analysis of the frame
    // rather than paying for it twice: the union of detected part boxes in
    // pixels, or the whole frame when nothing was detected, plus the lamps to
    // keep out of the glare score.
    const boxes = boxesFromDetections(detections, videoW, videoH);

    const q = await analyzeImageQuality(base64, boxes.car, {
      headlightBoxes: boxes.headlightBoxes,
      taillightBoxes: boxes.taillightBoxes,
    });
    const stats = statsFor(region);
    stats.seen += 1;
    if (q.blurry) stats.blurry += 1;
    else stats.sharp += 1;
    // The window counts on the popup's looser bar, not the upload gate's.
    noteWindowFrame(!!q.popupBlurry);

    lastVerdict = {
      blurred: !!q.blurry,
      unknown: false,
      blurScore: q.blurScore,
      tenengradScore: q.tenengradScore,
      // What the verdict was actually taken on, so a frame that was let through
      // and a frame that was blocked can be told apart by how close the cell
      // count came to the bar rather than by a single opaque boolean.
      blurryCells: q.blurryCells,
      gridCells: q.gridCells,
      glare: !!q.glare,
      region: region || "unknown",
      quality: q,
      t: Date.now(),
    };

    frames.push({
      blurred: lastVerdict.blurred,
      bs: q.blurScore,
      tg: q.tenengradScore,
      cells: q.blurryCells,
      total: q.gridCells,
      glare: !!q.glare,
      region: lastVerdict.region,
      t: hhmmss(),
    });
    if (frames.length > FRAME_LOG_SIZE) frames.shift();

    return lastVerdict;
  } catch (e) {
    // Never let a failed analysis block the inspection.
    lastVerdict = { blurred: false, unknown: true, region: region || "unknown" };
    return lastVerdict;
  } finally {
    analysing = false;
  }
};

/**
 * Feed one frame into the popup's window and nothing else.
 *
 * For a module whose per-frame loop does not call checkFrameForBlur -- the
 * stencil video only judges frames at the moment it keeps them -- so the window
 * still sees a steady stream of frames. It leaves the gate's verdict alone, so a
 * shot being judged for upload at the same time is never handed this frame's
 * answer. Not meant to be awaited; overlapping calls are skipped.
 */
export const sampleFrameForBlurWindow = async (
  base64,
  detections,
  videoW,
  videoH
) => {
  if (!notify || popupDone) return;
  if (!base64 || !videoW || !videoH) return;
  if (sampling) return;
  sampling = true;

  try {
    const boxes = boxesFromDetections(detections, videoW, videoH);
    const q = await analyzeImageQuality(base64, boxes.car, {
      headlightBoxes: boxes.headlightBoxes,
      taillightBoxes: boxes.taillightBoxes,
    });
    noteWindowFrame(!!q.popupBlurry);
  } catch (e) {
    // A frame that could not be analysed says nothing about the lens.
  } finally {
    sampling = false;
  }
};

/**
 * May the frame just analysed be uploaded?
 *
 * True while gating is off, true when the analysis could not run, and false only
 * when the last frame was positively judged blurry.
 */
export const mayUploadFrame = () => {
  if (mode !== MODES.BLUR) return true;
  if (!lastVerdict || lastVerdict.unknown) return true;
  return !lastVerdict.blurred;
};

/** Record that an upload was skipped, for the panel and the sheet. */
export const noteBlockedUpload = (region) => {
  blockedUploads += 1;
  pushEvent(`upload skipped (blurry) ${region || ""}`.trim());
};

export const getBlockedUploads = () => blockedUploads;
export const getLastVerdict = () => lastVerdict;

/**
 * Judge the last POPUP_WINDOW_SIZE frames: raise the popup when every one of
 * them came back blurry.
 *
 * Called from the detection loop on every analysed frame. Returns non-null on
 * exactly one call per inspection at most: the first one that finds a full window
 * of blurry frames. Every other call returns null -- while fewer than five frames
 * have been seen, while any of the last five was sharp, and on every frame after
 * the popup has been raised. resetBlur is the only thing that re-arms it, for a
 * new inspection.
 */
export const takeBlurWindow = () => {
  try {
    if (!notify || popupDone) return null;
    if (popupFrames.length < POPUP_WINDOW_SIZE) return null;

    const seen = popupFrames.length;
    const blurry = popupFrames.filter(Boolean).length;
    if (blurry < seen) return null;

    lastWindow = {
      seen,
      blurry,
      sharp: 0,
      ratio: 1,
      blurred: true,
      enough: true,
      at: Date.now(),
    };

    popupDone = true;
    alertRaised = true;
    pushEvent(`last ${seen} frames blurry -> ALERT`);
    return lastWindow;
  } catch (e) {
    return null;
  }
};

/** Stats for the window that raised the popup (what the popup shows). */
export const getLastBlurWindow = () => lastWindow;

export const isBlurAlertRaised = () => alertRaised;

/**
 * User has dismissed the popup. Only clears the flag: the popup is raised at most
 * once, so no new window is opened and it will not come back this inspection.
 */
export const acknowledgeBlurAlert = () => {
  alertRaised = false;
  pushEvent("blur alert acknowledged");
};

/**
 * Has every frame seen for this region been blurry?
 *
 * Only meaningful once at least one frame has been seen; a region nobody has
 * pointed the camera at yet is not "all blurry".
 */
export const isRegionAllBlurred = (region) => {
  const s = regionStats[region || "unknown"];
  return !!(s && s.seen > 0 && s.blurry === s.seen);
};

export const getRegionStats = (region) => {
  const s = regionStats[region || "unknown"];
  return s ? { ...s } : { seen: 0, blurry: 0, sharp: 0 };
};

export const getAllRegionStats = () => {
  const out = {};
  for (const k of Object.keys(regionStats)) out[k] = { ...regionStats[k] };
  return out;
};

/**
 * Should this region's segment be held grey on the strength of the last verdict?
 *
 * Only ever holds a segment that is still grey. A segment already drawn blue or
 * green is never taken back -- see `revealed`.
 */
const shouldHoldGrey = (regionsCovered, region) => {
  if (!region || !(region in regionsCovered)) return false;
  if (revealed[region]) return false;
  if (!lastVerdict || lastVerdict.unknown || !lastVerdict.blurred) return false;
  // The verdict must belong to THIS region. Without this check, moving to a new
  // region straight after a blurry frame would grey the new one on the strength
  // of the previous region's frame.
  return lastVerdict.region === region;
};

/**
 * A copy of the coverage map with the current region forced back to NOT_COVERED
 * while its frames are blurry, so the dial segment stays grey instead of turning
 * blue or green.
 *
 * One-way, and latched: this can only keep a segment from colouring in the first
 * place. Once a segment has actually been drawn blue or green it is recorded in
 * `revealed` and never masked again, so a blurry frame over a region the user has
 * already finished cannot take its colour away. Progress on the dial only ever
 * moves forwards, which is what the user is reading it for.
 *
 * A copy on purpose: the guidance module's own map is left untouched, so this only
 * changes what is drawn, never what the guidance believes. With gating off the map
 * is passed straight through.
 */
export const maskRegionsForBlur = (regionsCovered, region) => {
  if (mode !== MODES.BLUR) return regionsCovered;
  if (!regionsCovered || typeof regionsCovered !== "object") return regionsCovered;

  const masked = shouldHoldGrey(regionsCovered, region)
    ? { ...regionsCovered, [region]: 0 }
    : regionsCovered;

  // Latch on what is actually DRAWN, not on what guidance believes. On the frame
  // where a blurry region first turns covered, the mask above has already put it
  // back to grey, so it is not recorded here and the hold survives into the next
  // frame. It is recorded the first time it reaches the dial with a colour.
  for (const key of Object.keys(masked))
    if (masked[key] !== 0) revealed[key] = true;

  return masked;
};

/** Per-frame fields for logging, while gating is on. */
export const getSheetFields = (region) => {
  const stats = getRegionStats(region);
  return {
    mode,
    notify,
    blurred: lastVerdict ? !!lastVerdict.blurred : null,
    blurryCells: lastVerdict ? lastVerdict.blurryCells : null,
    gridCells: lastVerdict ? lastVerdict.gridCells : null,
    glare: lastVerdict ? !!lastVerdict.glare : null,
    uploadBlocked: mode === MODES.BLUR && !mayUploadFrame(),
    region: region || "",
    regionSeen: stats.seen,
    regionBlurry: stats.blurry,
    regionAllBlur: isRegionAllBlurred(region),
  };
};

/** HTML for the on-screen panel: mode, the last frames, and per-region tallies. */
export const getBlurDebugHtml = () => {
  try {
    if (mode === MODES.NORMAL)
      return notify
        ? "<b>BLUR NOTIFY</b> &nbsp; <i>popup only, no blur gating</i>"
        : "<b>NORMAL MODE</b> &nbsp; <i>no torch, no blur gating</i>";
    if (mode !== MODES.BLUR)
      return "<b>torch mode</b> &nbsp; <i>blur checks idle</i>";
    const modeLabel = '<span style="color:#ffab40"><b>BLUR MODE</b></span>';

    const rows = frames
      .map(
        (f, i) =>
          `${i + 1}. ${f.blurred ? "BLUR" : "ok  "} ${f.cells}/${f.total} cells ` +
          `bs=${f.bs} tg=${f.tg}${f.glare ? " GLARE" : ""} ${f.region}`
      )
      .join("<br/>");

    const regions = Object.keys(regionStats)
      .map((r) => {
        const s = regionStats[r];
        const all = s.seen > 0 && s.blurry === s.seen ? " ALL-BLUR" : "";
        return `${r}: ${s.blurry}/${s.seen} blurry${all}`;
      })
      .join("<br/>");

    return (
      `${modeLabel} &nbsp; <b>skipped ${blockedUploads}</b><br/>` +
      `<b>last ${frames.length} frames:</b><br/>${rows || "-"}<br/>` +
      `<b>per region:</b><br/>${regions || "-"}<br/>` +
      `<b>events:</b><br/>${events.slice(-5).join("<br/>") || "-"}`
    );
  } catch (e) {
    return "";
  }
};
